import { useEffect, useState } from "react"

import { InfoBox } from "../../../components/InfoBox"
import { Link } from "../../../components/Link"
import { InfoIcon } from "../../../components/svgs"
import { asset_url } from "../../../utils/asset_url"
import { is_narrow_screen } from "../../../utils/screen_type"
import { land_or_sea_types } from "../../data/coverage_land/uk/data"
import { OIL_GAS_RESERVES_CONFIDENCE, OIL_GAS_RESOURCES_CONFIDENCE } from "../../data/fossil_fuels/process_data_component"
import pub_sub from "../../state/pub_sub"
import { InfoSectionId } from "../../state/pub_sub/interface"
import { RenderSingleTile } from "../footer/RenderSingleTile"
import { CellDataV1 } from "../interface"
import "./ui.css"
const SEWTHA_url = asset_url("/imgs/SEWTHA_book_cover.png")
const bgs_url = asset_url("/imgs/logos_BGS.png")
const ons_url = asset_url("/svgs/logos_ONS.svg")
const ukceh_url = asset_url("/imgs/logos_UKCEH.png")
const tiles_1_url = asset_url("/imgs/tiles_1.png")
const tiles_2_url = asset_url("/imgs/tiles_2.png")
const solar_capacity_demo_url = asset_url("/imgs/solar_capacity_demo.png")
const wind_capacity_demo_url = asset_url("/imgs/wind_capacity_demo.png")


export function Info()
{
    const [show_info_box, set_show_info_box] = useState<InfoSectionId | boolean>(false)

    useEffect(() => pub_sub.sub("show_info_and_data_sources", set_show_info_box), [])

    useEffect(() => {
        if (!show_info_box || show_info_box === true) return

        // Scroll to the relevant section of the info box if it's already open
        const id = "info_section_" + show_info_box
        const element = document.getElementById(id)
        if (element) element.scrollIntoView({ behavior: "smooth" })
    }, [show_info_box])

    return <div
        className="ui_button"
        onClick={() => set_show_info_box(true)}
    >
        <span>
            {is_narrow_screen() ? "" : "Sources "}<InfoIcon style={{ marginLeft: is_narrow_screen() ? 0 : 5 }} />
        </span>

        {show_info_box && <InfoBox
            wider_info_box={true}
            message={<>
                <h1>Data Sources <InfoIcon style={{ height: 30 }} /></h1>

                <div style={{ overflowY: "scroll", maxHeight: "50vh", paddingRight: 10 }}>
                    <p>
                        This is a simplified simulation of the UK's energy system, focused
                        on renewable energy generation and demand.
                    </p>

                    <Section id="map" title="The map" />
                    <p>
                        The map shows the different types of land and sea in the UK:
                    </p>
                    <ExampleTileTypes />
                    <p>
                        The map is divided into 400 squares, each representing about
                        35 km × 35 km (≈ 1250 km²) of land or sea. Only ⅓ of the sea
                        in the UK's exclusive economic zone (sea) is shown, so the real
                        area of the UK land and sea is twice as large.  The UK is mostly sea!
                    </p>
                    <p>
                        The <Link url="https://wikisim.org/wiki/1261">data for the map</Link> is based on
                        the land coverage data from
                        the UK's Centre for Ecology and Hydrology
                    </p>
                    <LogoImg src={ukceh_url} alt="UKCEH logo" url="https://www.ceh.ac.uk" />

                    <h2>
                        The sea areas
                    </h2>
                    <ul>
                        <li>
                            The <Link url="https://wikisim.org/wiki/1267">UK's exclusive economic zone (EEZ)</Link> is from
                            Sea Around Us - a research initiative at the University of British Columbia and the University of Western Australia.
                        </li>
                        <li>
                            The <Link url="https://wikisim.org/wiki/1268">UK's offshore shallow area</Link> from
                            the University of Southampton's IROE (Intelligent and Resilient Ocean Engineering) group
                            referencing data from British Geological Survey
                        </li>
                    </ul>

                    <LogoImg src={bgs_url} alt="BGS logo" url="https://www.bgs.ac.uk"/>


                    <Section id="capacity_factors" title="Wind and Solar Capacity Factors" />
                    <p>
                        The blue and yellow colours for wind and solar on the map represent
                        how much electricity would be generated by wind and solar power plants
                        in that area at that moment in time.
                    </p>
                    <div style={{ display: "flex", flexDirection: "row", gap: 10, justifyContent: "center" }}>
                        <img src={wind_capacity_demo_url} style={{ width: 200 }} />
                        <img src={solar_capacity_demo_url} style={{ width: 200 }} />
                    </div>
                    <p>
                        This data comes from <a href="https://www.renewables.ninja/about">renewables.ninja</a> at
                        the Centre for Environmental Policy, Imperial College London.
                    </p>
                    <LogoImg src={"data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI4LjEuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAxNTQ4IDE3MCIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMTU0OCAxNzA7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KCS5zdDB7ZmlsbDojMDAwMENEO30KPC9zdHlsZT4KPGc+Cgk8cG9seWdvbiBjbGFzcz0ic3QwIiBwb2ludHM9IjY0MS45NywwIDY0MS45NywxNzAgNzUyLjQ3LDE3MCA3NTIuNDcsMTQwLjI1IDY3NC4yNywxNDAuMjUgNjc0LjI3LDk3Ljc1IDc0NS42Nyw5Ny43NSA3NDUuNjcsNjguODUgCgkJNjc0LjI3LDY4Ljg1IDY3NC4yNywyOS43NSA3NTIuNDcsMjkuNzUgNzUyLjQ3LDAgCSIvPgoJPGc+CgkJPHBhdGggY2xhc3M9InN0MCIgZD0iTTUwNy43LDI3LjQ1aC0yMy44djU3LjI5aDIzLjhjMTguNjIsMCwzMi4zLTkuMDEsMzIuMy0yOC42NUM1NDAsMzUuNyw1MjYuMzIsMjcuNDUsNTA3LjcsMjcuNDUgTTUwOS40LDExMi4yCgkJCWgtMjUuNVYxNzBoLTMyLjNWMGg1Ny44QzU0NS42NSwwLDU3NCwxNi40Miw1NzQsNTYuMUM1NzQsOTUuMiw1NDUuNDQsMTEyLjIsNTA5LjQsMTEyLjIiLz4KCTwvZz4KCTxwb2x5Z29uIGNsYXNzPSJzdDAiIHBvaW50cz0iMTAzMi45OCwwIDEwMzIuOTgsMjkuNzUgMTA3Mi45MywyOS43NSAxMDcyLjkzLDE0MC4yNSAxMDMyLjk4LDE0MC4yNSAxMDMyLjk4LDE3MCAxMTQ1LjE4LDE3MCAKCQkxMTQ1LjE4LDE0MC4yNSAxMTA1LjIzLDE0MC4yNSAxMTA1LjIzLDI5Ljc1IDExNDUuMTgsMjkuNzUgMTE0NS4xOCwwIAkiLz4KCTxnPgoJCTxwYXRoIGNsYXNzPSJzdDAiIGQ9Ik05MjQuNjIsNTIuN2MwLDIwLjQ4LTExLjksMjYuOTQtMzIuMywyNi45NGgtMjcuMlYyNy40NmgyNy4yQzkxNC40NiwyNy40Niw5MjQuNjIsMzUuNyw5MjQuNjIsNTIuNwoJCQkgTTk1OC42Miw1Mi43YzAtMzkuNjgtMjcuMi01Mi43LTYyLjktNTIuN2gtNjIuOXYxNzBoMzIuM3YtNjIuOWgyNy4yYzEuNDksMCwyLjk3LTAuMDMsNC40NC0wLjA4TDkyOS42NywxNzBoMzUuNzlsLTM2LjUxLTY5LjcyCgkJCUM5NDYuNzEsOTIuMzksOTU4LjYyLDc3LjIzLDk1OC42Miw1Mi43Ii8+Cgk8L2c+Cgk8cGF0aCBjbGFzcz0ic3QwIiBkPSJNMTI4Ni45LDMwLjE2bDIzLjI0LDc0Ljk4aC00Ni40OUwxMjg2LjksMzAuMTZ6IE0xMjY1LjY1LDBsLTU2LjEsMTcwaDM0bDExLjU5LTM3LjRoNjMuNTFsMTEuNTksMzcuNGgzNAoJCWwtNTYuMS0xNzBIMTI2NS42NXoiLz4KCTxwb2x5Z29uIGNsYXNzPSJzdDAiIHBvaW50cz0iMTQzNy4xNiwwIDE0MzcuMTYsMTcwIDE1NDcuOTQsMTcwIDE1NDcuOTQsMTQwLjI1IDE0NjkuNDYsMTQwLjI1IDE0NjkuNDYsMCAJIi8+Cgk8cG9seWdvbiBjbGFzcz0ic3QwIiBwb2ludHM9IjMyMy41MywwIDI4Mi43Myw5OS45NiAyNDEuOTMsMCAxOTYuMDMsMCAxOTYuMDMsMTcwIDIyNi42MywxNzAgMjI2LjYzLDQyLjkgMjY1LjczLDEzMi42IAoJCTI2Ny40MywxMzIuNiAyOTguMDMsMTMyLjYgMjk5LjczLDEzMi42IDMzOC44Myw0Mi45IDMzOC44MywxNzAgMzY5LjQzLDE3MCAzNjkuNDMsMCAJIi8+Cgk8cG9seWdvbiBjbGFzcz0ic3QwIiBwb2ludHM9IjAuMDYsMCAwLjA2LDI5Ljc1IDQwLjAxLDI5Ljc1IDQwLjAxLDE0MC4yNSAwLjA2LDE0MC4yNSAwLjA2LDE3MCAxMTIuMjYsMTcwIDExMi4yNiwxNDAuMjUgCgkJNzIuMzEsMTQwLjI1IDcyLjMxLDI5Ljc1IDExMi4yNiwyOS43NSAxMTIuMjYsMCAJIi8+CjwvZz4KPC9zdmc+Cg=="} alt="London Imperial logo" url="https://www.imperial.ac.uk/environmental-policy/" />


                    <Section id="population" title="Population" />
                    <p>
                        The <Link url="https://wikisim.org/wiki/1011">UK population data</Link> is from
                        the UK's Office for National Statistics
                    </p>
                    <LogoImg src={ons_url} alt="ONS logo" url="https://www.ons.gov.uk/" height={43} />


                    <Section id="power_demand" title="Power demand" />
                    <p>
                        The total power demand of the UK is about 500 to 600 GW.
                    </p>
                    <p>
                        Calculating the total power demand of the UK is a
                        complicated topic and the value used in this smulation is
                        largely based on the
                        2009 book by Professor David MacKay "Sustainable Energy - without the hot air", with
                        some minor adjustments and updates e.g. to include data centre
                        power usage.
                    </p>
                    <LogoImg
                        src={SEWTHA_url}
                        alt="Sustainable Energy - without the hot air book cover"
                        url="https://www.withouthotair.com/"
                        height={100}
                    />
                    <p>
                        The specific value used in this simulation comes from
                        the <code>"total_demand"</code> value you can see in the <Link url="https://wikisim.org/wiki/1239">power demand data</Link>.
                    </p>
                    <p>
                        Its units are in kW hours per person per day.  To convert into
                        the GW value shown in the simulation, it is multiplied by the population as well as
                        converting from kW hours per day to GW.
                    </p>


                    <Section id="power_supply" title="Power Supply" />
                    <p>
                        The power supply data is similarly based on Professor David MacKay's
                        book.  In this case rather than using the single high level "total_supply"
                        value, instead the component values of the calculations are used to calculate the power supply.
                    </p>
                    <p>
                        For example:
                    </p>
                    <ul>
                        <li>
                            The <Link url="https://wikisim.org/wiki/1276">offshore wind power density of 3 W/m²</Link>
                        </li>
                        <li>
                            The <Link url="https://wikisim.org/wiki/1275">onshore wind power density of 2 W/m²</Link>
                        </li>
                        <li>
                            The <Link url="https://wikisim.org/wiki/1204">solar PV farm power density of 5 W/m²</Link>
                        </li>
                        <li>
                            The <Link url="https://wikisim.org/wiki/1201">residential PV power density of 22 W/m²</Link> which is
                            used in conjunction with the <Link url="https://wikisim.org/wiki/1273">Ratio of south facing roofs in UK to area of built up land</Link> to get the
                            <Link url="https://wikisim.org/wiki/1274">Potential solar PV power for built areas in UK</Link> of 0.63 W/m²
                        </li>
                    </ul>


                    <Section id="oil_and_gas_data" title="Oil and Gas Data" />
                    <p>
                        Data on reserves, resources and production is from
                        the <a href="https://wikisim.org/wiki/1283">
                            North Sea Transition Authority
                        </a>.  Data is contextualised and processed here: <a href="https://wikisim.org/wiki/1284v19">
                            UK oil and gas production, reserves and resources
                        </a>.  Specifically this sim uses the {OIL_GAS_RESERVES_CONFIDENCE} reserves
                        and the {OIL_GAS_RESOURCES_CONFIDENCE} resources (to set an maximal
                        realistic upper bound).  See <a href="https://wikisim.org/wiki/1291v5">
                            Forecast of potential UKCS production rates
                        </a> by Carys Thomas for more details including justification for 3C resources.
                    </p>

                    <Section id="motivation" title="Motivation for this sim" />
                    <p>
                        To get a better intuitive understanding of the scale of
                        energy demand and the resources and area required to
                        build renewable energy generation or other sources of energy.
                    </p>

                    <p>
                        It's dedicated to the
                        memory of Professor David MacKay, who inspired so many of us.  And given
                        with thanks to all our teachers, friends and supporters.
                    </p>
                </div>
                </>
            }
            on_close={() => set_show_info_box(false)}
        />}
    </div>
}


function Section(props: { id: InfoSectionId, title: string })
{
    return <p style={{ marginTop: 50 }} id={"info_section_" + props.id }>
        <b style={{ fontSize: "var(--font-medium)" }}>
            {props.title.toUpperCase()}
        </b>
    </p>
}


function LogoImg(props: { src: string, alt: string, url: string, height?: number })
{
    const { height = 50 } = props

    return <p style={{ textAlign: "center" }}>
        <Link url={props.url}>
            <img src={props.src} style={{ margin: "auto", height }} />
        </Link>
    </p>
}


function ExampleTileTypes()
{
    const use_images = true
    if (use_images) return <div style={{ display: "flex" }}>
        <img src={tiles_1_url} style={{ width: 100 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 40, padding: "22px 0" }}>
            <div>{land_or_sea_types.woodland.human_readable}</div>
            <div>{land_or_sea_types.arable.human_readable}</div>
            <div>{land_or_sea_types.grassland.human_readable}</div>
            <div>{land_or_sea_types.suburban.human_readable}</div>
            <div>{land_or_sea_types.urban.human_readable}</div>
        </div>
        <div style={{ width: 40 }}></div>
        <img src={tiles_2_url} style={{ width: 100 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 40, padding: "22px 0" }}>
            <div>{land_or_sea_types.rock.human_readable}</div>
            <div>{land_or_sea_types.wetland.human_readable}</div>
            <div>{land_or_sea_types.inland_water.human_readable}</div>
            <div>{land_or_sea_types.shallow.human_readable}</div>
            <div>{land_or_sea_types.deep.human_readable}</div>
        </div>
    </div>

    const urban: CellDataV1 = {
        id: 4, x: 1, y: 1,
        type: "land", subtype: "urban",
        has_wind_turbine: false,
        has_solar_farm: false,
        has_oil_rig: undefined,
        has_oil_pocket: undefined,
    }

    const tile_data = Object.values(land_or_sea_types).map(data => ({ ...urban, ...data }))

    return <div style={{ display: "flex", gap: 0, flexWrap: "wrap", flexDirection: "column" }}>
        {tile_data.map((tile_data, index) =>
            <RenderSingleTile key={index} tile_data={tile_data} size={50} border="" />
        )}
    </div>
}
